import React, { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import ImageCropper from '../common/ImageCropper';
import toast from 'react-hot-toast';

interface ImageUploadWithCropProps {
    onImageReady: (file: File) => void;
}

type Mode = 'initial' | 'camera' | 'crop' | 'submitted';

const ImageUploadWithCrop: React.FC<ImageUploadWithCropProps> = ({ onImageReady }) => {
    const [mode, setMode] = useState<Mode>('initial');
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Start camera
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            streamRef.current = stream;
            setMode('camera');
        } catch (err) {
            console.error("Error accessing camera:", err);
            toast.error("Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.");
        }
    };

    // Stop camera
    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setMode('initial');
    };

    // Capture image from camera
    const captureImage = () => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(videoRef.current, 0, 0);
                canvas.toBlob((blob) => {
                    if (blob) {
                        stopCamera();
                        const url = URL.createObjectURL(blob);
                        setImagePreview(url);
                        setMode('crop');
                    }
                }, 'image/jpeg');
            }
        }
    };

    // Handle file selection
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const url = URL.createObjectURL(file);
            setImagePreview(url);
            setMode('crop');
        }
        // Reset input
        if (e.target) {
            e.target.value = '';
        }
    };

    // Handle crop complete
    const handleCropComplete = async (croppedBlob: Blob) => {
        console.log('✂️ [ImageUploadWithCrop] Crop completed');
        const file = new File([croppedBlob], "submission.jpg", { type: "image/jpeg" });

        // Set submitted mode to show loading
        setMode('submitted');
        console.log('📤 [ImageUploadWithCrop] Sending file to parent');

        // Send to parent
        onImageReady(file);
    };

    // Handle crop cancel
    const handleCancelCrop = () => {
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(null);
        setMode('initial');
    };

    // Render submitted/loading state
    if (mode === 'submitted') {
        return (
            <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-blue-200 rounded-xl bg-blue-50">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-lg font-medium text-blue-900">Đang xử lý...</p>
                <p className="text-sm text-blue-600 mt-2">Vui lòng đợi</p>
            </div>
        );
    }

    // Render initial upload UI
    if (mode === 'initial') {
        return (
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8" />
                </div>
                <p className="text-gray-900 font-medium mb-2">Tải ảnh bài làm lên</p>
                <p className="text-sm text-gray-500 mb-4">Hỗ trợ JPG, PNG</p>

                <div className="flex gap-3">
                    <button
                        onClick={startCamera}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Camera className="w-4 h-4" />
                        Chụp ảnh
                    </button>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Chọn ảnh
                    </button>
                </div>

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                />
            </div>
        );
    }

    // Render camera view
    if (mode === 'camera') {
        return (
            <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                    <button
                        onClick={stopCamera}
                        className="p-3 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/30 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <button
                        onClick={captureImage}
                        className="p-4 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors shadow-lg"
                    >
                        <Camera className="w-8 h-8" />
                    </button>
                </div>
            </div>
        );
    }

    // Render crop view
    if (mode === 'crop' && imagePreview) {
        return (
            <ImageCropper
                imageUrl={imagePreview}
                onCropComplete={handleCropComplete}
                onCancel={handleCancelCrop}
            />
        );
    }

    return null;
};

export default ImageUploadWithCrop;
